import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { apiGet, apiPost } from '../../lib/api.js';
import {
  AlertTriangle, RefreshCw, ChevronLeft, ChevronDown, ChevronRight,
  ShieldCheck, ShieldAlert, ShieldX,
} from 'lucide-react';

// ── Dimension categories (v2 / 47-dim) ──────────────────────────────────
// Each entry: { key, label, type: 'llm' | 'mechanic' | 'computed' }.
// Categories rendered in this order; hidden if every dim is "not applicable".

const CATEGORIES = [
  {
    id: 'fundamentals',
    title: 'Sales fundamentals',
    accent: 'civix-cyan',
    dims: [
      { key: 'rapport',             label: 'Rapport',              type: 'llm' },
      { key: 'discovery',           label: 'Discovery',            type: 'llm' },
      { key: 'objectionHandling',   label: 'Objection handling',   type: 'llm' },
      { key: 'pricingFraming',      label: 'Pricing framing',      type: 'llm' },
      { key: 'urgencyCreation',     label: 'Urgency creation',     type: 'llm' },
      { key: 'closeAttemptQuality', label: 'Close attempt quality',type: 'llm' },
      { key: 'emotionRegulation',   label: 'Emotion regulation',   type: 'llm' },
      { key: 'compliance',          label: 'Compliance (general)', type: 'llm' },
    ],
  },
  {
    id: 'mechanics',
    title: 'Sales mechanics',
    accent: 'civix-purple',
    dims: [
      { key: 'callOpenStrength',         label: 'Call open strength',    type: 'llm' },
      { key: 'callCloseStrength',        label: 'Call close strength',   type: 'llm' },
      { key: 'recapAccuracy',            label: 'Recap accuracy',        type: 'llm' },
      { key: 'tieDownCount',             label: 'Tie-down count',        type: 'mechanic', unit: '' },
      { key: 'listenToTalkRatio',        label: 'Listen/talk ratio',     type: 'mechanic', unit: '' },
      { key: 'fillerWordRatio',          label: 'Filler-word ratio',     type: 'mechanic', unit: '' },
      { key: 'monologueLengthMaxSec',    label: 'Max monologue',         type: 'mechanic', unit: 's' },
      { key: 'interruptCount',           label: 'Interrupts',            type: 'mechanic', unit: '' },
      { key: 'talkPaceWordsPerMin',      label: 'Talk pace (wpm)',       type: 'mechanic', unit: ' wpm' },
      { key: 'averageResponseLatencySec',label: 'Avg response latency',  type: 'mechanic', unit: 's' },
      { key: 'agentSilenceLongestSec',   label: 'Longest silence',       type: 'mechanic', unit: 's' },
      { key: 'crossTalkSeconds',         label: 'Cross-talk',            type: 'mechanic', unit: 's' },
    ],
  },
  {
    id: 'insurance-compliance',
    title: 'Insurance compliance',
    accent: 'rose',
    dims: [
      { key: 'tcpaIdentification',                  label: 'TCPA identification',           type: 'llm' },
      { key: 'recordedLineDisclosure',              label: 'Recorded-line disclosure',      type: 'llm' },
      { key: 'notMedicareDisclosure',               label: '"Not Medicare" disclosure',     type: 'llm' },
      { key: 'agentLicensedDisclosure',             label: 'Agent licensed disclosure',     type: 'llm' },
      { key: 'replacementDisclosure',               label: 'Replacement disclosure',        type: 'llm' },
      { key: 'suitabilityCheck',                    label: 'Suitability check',             type: 'llm' },
      { key: 'premiumDisclosure',                   label: 'Premium disclosure',            type: 'llm' },
      { key: 'paymentMethodDisclosure',             label: 'Payment-method disclosure',     type: 'llm' },
      { key: 'freeRightToCancel',                   label: 'Free-look right to cancel',     type: 'llm' },
      { key: 'nonGuaranteedIllustrationDisclaimer', label: 'Non-guaranteed illustration',   type: 'llm' },
    ],
  },
  {
    id: 'carrier',
    title: 'Carrier knowledge',
    accent: 'amber',
    dims: [
      { key: 'carrierProductMatch',           label: 'Carrier product match',         type: 'llm' },
      { key: 'carrierUnderwritingAccuracy',   label: 'Carrier underwriting accuracy', type: 'llm' },
      { key: 'carrierRateAccuracy',           label: 'Carrier rate accuracy',         type: 'llm' },
      { key: 'carrierContestabilityClarity',  label: 'Contestability clarity',        type: 'llm' },
      { key: 'carrierExclusionsCovered',      label: 'Exclusions covered',            type: 'llm' },
      { key: 'carrierRidersOffered',          label: 'Riders offered',                type: 'llm' },
      { key: 'carrierAppCompletenessSignal',  label: 'Application path set up',       type: 'llm' },
      { key: 'carrierMisrepresentationRisk',  label: 'Misrepresentation risk',        type: 'llm', inverse: true },
    ],
  },
  {
    id: 'cx',
    title: 'Customer experience',
    accent: 'emerald',
    dims: [
      { key: 'activeListeningSignals',     label: 'Active listening',     type: 'llm' },
      { key: 'empathyExpression',          label: 'Empathy expression',   type: 'llm' },
      { key: 'trustBuildingBehaviors',     label: 'Trust building',       type: 'llm' },
      { key: 'transparencyScore',          label: 'Transparency',         type: 'llm' },
      { key: 'prospectComfortTrend',       label: 'Prospect comfort trend',type: 'llm' },
      { key: 'callExperiencePredictedNPS', label: 'Predicted NPS',        type: 'llm' },
    ],
  },
  {
    id: 'outcome',
    title: 'Outcome predictors',
    accent: 'civix-cyan',
    dims: [
      { key: 'predictedCloseConfidence', label: 'Predicted close confidence', type: 'llm' },
      { key: 'complianceRiskComposite',  label: 'Compliance risk composite',  type: 'computed', inverse: true },
    ],
  },
];

const NEXT_STEP_LABEL = {
  book_appt:  'Book appointment',
  send_quote: 'Send quote',
  nurture:    'Nurture',
  dnc:        'Add to DNC',
  callback:   'Callback',
  no_action:  'No action',
};

function fmtTs(sec) {
  const s = Math.max(0, Math.floor(Number(sec) || 0));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, '0')}`;
}

function ScoreCircle({ score }) {
  const s = Math.max(0, Math.min(100, Number(score) || 0));
  const tone =
    s >= 80 ? 'text-emerald-300' : s >= 60 ? 'text-amber-300' : 'text-rose-300';
  return (
    <div className={`flex flex-col items-center justify-center w-32 h-32 rounded-full border-4 ${tone} border-current`}>
      <div className={`text-4xl font-display font-bold ${tone}`}>{s}</div>
      <div className="text-xs text-white/50 uppercase tracking-wider">Overall</div>
    </div>
  );
}

function Bar({ label, value, max = 100, mono = false, rationale, naApplicable }) {
  const pct = Math.max(0, Math.min(100, (Number(value) / max) * 100 || 0));
  const tone = naApplicable
    ? 'bg-white/10'
    : pct >= 80 ? 'bg-emerald-400/70'
    : pct >= 60 ? 'bg-amber-400/70'
    : 'bg-rose-400/70';
  return (
    <div title={rationale || ''}>
      <div className="flex justify-between text-xs text-white/60 mb-1">
        <span className="flex items-center gap-2">
          {label}
          {naApplicable && <span className="text-[10px] uppercase text-white/40 italic">n/a</span>}
        </span>
        <span className={`font-mono ${mono ? 'text-white/70' : 'text-white/80'}`}>{value}</span>
      </div>
      <div className="h-2 rounded-sm bg-white/5 overflow-hidden">
        <div className={`h-full ${tone}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function isNotApplicable(rationale) {
  return typeof rationale === 'string' && /^not applicable/i.test(rationale.trim());
}

function ComplianceRiskBadge({ risk }) {
  const r = Math.max(0, Math.min(100, Number(risk) || 0));
  let tone = 'border-emerald-400/60 text-emerald-300 bg-emerald-400/10';
  let Icon = ShieldCheck;
  let label = 'Low compliance risk';
  if (r >= 60) { tone = 'border-rose-500/60 text-rose-300 bg-rose-500/10'; Icon = ShieldX; label = 'HIGH compliance risk'; }
  else if (r >= 30) { tone = 'border-amber-400/60 text-amber-300 bg-amber-400/10'; Icon = ShieldAlert; label = 'Moderate compliance risk'; }
  return (
    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-sm border ${tone} text-sm`}>
      <Icon className="w-4 h-4" />
      <span className="font-display font-bold">{label}</span>
      <span className="font-mono text-xs opacity-80">{r}/100</span>
    </div>
  );
}

function CategoryPanel({ category, dims, rationales, scIsLegacy }) {
  const [open, setOpen] = useState(false);
  // Determine if all dims in this category are "not applicable".
  const naCount = category.dims.filter((d) => {
    if (d.type !== 'llm') return false;
    return isNotApplicable(rationales?.[d.key]);
  }).length;
  const allNA = naCount > 0 && naCount === category.dims.filter((d) => d.type === 'llm').length;
  if (allNA) return null;

  const sample = category.dims.slice(0, 3);
  return (
    <div className="panel p-5">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between text-left"
      >
        <div className="font-display font-bold">{category.title}</div>
        <div className="text-white/40">{open ? <ChevronDown className="w-4 h-4"/> : <ChevronRight className="w-4 h-4"/>}</div>
      </button>
      {!open && (
        <div className="mt-3 grid grid-cols-3 gap-3 text-xs">
          {sample.map((d) => (
            <div key={d.key}>
              <div className="text-white/50 truncate">{d.label}</div>
              <div className="font-mono text-white/80">{dims?.[d.key] ?? 0}{d.unit || ''}</div>
            </div>
          ))}
        </div>
      )}
      {open && (
        <div className="mt-4 space-y-3">
          {category.dims.map((d) => {
            const rationale = rationales?.[d.key];
            const na = isNotApplicable(rationale);
            const value = dims?.[d.key] ?? 0;
            return (
              <div key={d.key}>
                <Bar
                  label={d.label}
                  value={value}
                  rationale={rationale}
                  naApplicable={na}
                  max={d.type === 'mechanic' && d.key === 'monologueLengthMaxSec' ? 120 : 100}
                />
                {rationale && (
                  <div className="text-[11px] text-white/45 mt-0.5 italic">
                    {rationale}
                  </div>
                )}
              </div>
            );
          })}
          {scIsLegacy && (
            <div className="text-xs text-white/40">Some v2 dims unavailable on this v1 scorecard.</div>
          )}
        </div>
      )}
    </div>
  );
}

function topN(arr, n) {
  return (Array.isArray(arr) ? arr : []).slice(0, n);
}

function rankDims(dims, rationales) {
  // Rank LLM-graded dims by score (skipping not-applicable). Return top/bot.
  const llmDims = CATEGORIES.flatMap((c) => c.dims).filter((d) => d.type === 'llm');
  const scored = llmDims
    .filter((d) => !isNotApplicable(rationales?.[d.key]))
    .map((d) => {
      let s = Number(dims?.[d.key]) || 0;
      if (d.inverse) s = 100 - s;
      return { ...d, score: s };
    });
  const sorted = [...scored].sort((a, b) => b.score - a.score);
  return { top: sorted.slice(0, 3), bottom: [...sorted].reverse().slice(0, 3) };
}

export default function CallScorecardView() {
  const { callId } = useParams();
  const nav = useNavigate();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['scorecard', callId],
    queryFn: () => apiGet(`/scorecards/${callId}`),
    retry: false,
  });

  const regen = useMutation({
    mutationFn: () => apiPost(`/scorecards/${callId}/regenerate`, {}),
    onSuccess: () => {
      toast.success('Re-grading enqueued — check back in ~1 minute');
      setTimeout(() => refetch(), 5000);
    },
    onError: (e) => toast.error(e.message || 'Failed to regenerate'),
  });

  if (isLoading) return <div className="p-8 text-white/60">Loading scorecard…</div>;
  if (error || !data?.scorecard) {
    return (
      <div className="p-8">
        <Link to="/dashboard" className="text-civix-cyan inline-flex items-center gap-1 text-sm">
          <ChevronLeft className="w-4 h-4" /> Back
        </Link>
        <div className="panel p-6 mt-4">
          <div className="text-white/70">No scorecard yet for this call.</div>
          <button
            onClick={() => regen.mutate()}
            disabled={regen.isPending}
            className="btn-primary mt-3 inline-flex items-center gap-1 text-sm"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Generate
          </button>
        </div>
      </div>
    );
  }

  const sc = data.scorecard;
  const dims = sc.dimensions || {};
  const rationales = sc.dimensionRationales || {};
  const violations = (sc.complianceFlags || []).filter((f) => f.severity === 'violation');
  const warns = (sc.complianceFlags || []).filter((f) => f.severity === 'warn');
  const schemaVer = Number(sc.schemaVersion || 1);
  const isLegacy = schemaVer < 2;
  const composite = Number(dims.complianceRiskComposite) || 0;

  const ranks = isLegacy ? { top: [], bottom: [] } : rankDims(dims, rationales);

  function actNextStep() {
    const ns = sc.recommendedNextStep || 'no_action';
    if (ns === 'book_appt') {
      // Wired to the real calendar page so the user can schedule immediately.
      // (audit-2026-06-05 P2-5)
      toast.success('Opening booking flow…');
      nav('/calendar');
    } else if (ns === 'send_quote') {
      toast('Send quote — coming soon', { icon: '📨' });
    } else if (ns === 'dnc') {
      toast.error('Marked for DNC review');
    } else if (ns === 'callback') {
      toast.success('Callback scheduled');
    } else {
      toast(`Next step: ${NEXT_STEP_LABEL[ns] || ns}`);
    }
  }

  return (
    <div className="p-6 space-y-4 max-w-6xl">
      <div className="flex items-center justify-between">
        <Link to="/dashboard" className="text-civix-cyan inline-flex items-center gap-1 text-sm">
          <ChevronLeft className="w-4 h-4" /> Back
        </Link>
        <div className="flex items-center gap-2">
          {isLegacy && (
            <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border border-white/20 text-white/60">
              v1 (legacy)
            </span>
          )}
          <button
            onClick={() => regen.mutate()}
            disabled={regen.isPending}
            className="btn-ghost text-xs inline-flex items-center gap-1"
          >
            <RefreshCw className="w-3 h-3" /> Re-grade
          </button>
        </div>
      </div>

      {sc.error && (
        <div className="panel p-4 border border-rose-500/40 bg-rose-500/10">
          <div className="inline-flex items-center gap-2 text-rose-300">
            <AlertTriangle className="w-4 h-4" /> Grading error: {sc.error}
          </div>
        </div>
      )}

      {/* Top strip: overall + risk + recommended next step */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="panel p-5 flex flex-col items-center justify-center gap-3">
          <ScoreCircle score={sc.overallScore} />
          <div className="text-xs text-white/50">
            model: {sc.model || '—'} · prompt: {sc.promptVersion} · schema: v{schemaVer}
          </div>
        </div>

        <div className="panel p-5 md:col-span-2 flex flex-col gap-3 justify-between">
          <div className="flex items-center justify-between">
            <div className="font-display font-bold">Compliance</div>
            {!isLegacy && <ComplianceRiskBadge risk={composite} />}
          </div>

          {!isLegacy && (
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <div className="text-xs text-white/50 mb-1">Recommended next step</div>
                <div className="inline-flex items-center gap-2">
                  <span className="px-3 py-1.5 rounded-sm bg-civix-cyan/20 text-civix-cyan font-display font-bold">
                    {NEXT_STEP_LABEL[sc.recommendedNextStep] || 'No action'}
                  </span>
                  <button onClick={actNextStep} className="btn-primary text-xs">
                    Take action
                  </button>
                </div>
              </div>
              <div className="text-xs text-white/50 max-w-sm">
                Predicted close confidence:{' '}
                <span className="font-mono text-white/80">
                  {dims.predictedCloseConfidence ?? 0}
                </span>
              </div>
            </div>
          )}

          {(violations.length > 0 || warns.length > 0) && (
            <div className="mt-1">
              <div className="font-display font-bold mb-1 text-amber-300 inline-flex items-center gap-2 text-sm">
                <AlertTriangle className="w-4 h-4" /> Compliance flags
              </div>
              <ul className="space-y-1 text-sm">
                {[...violations, ...warns].slice(0, 5).map((f, i) => (
                  <li key={i} className="flex gap-2">
                    <span className={`text-xs px-2 py-0.5 rounded-sm ${f.severity === 'violation' ? 'bg-rose-500/20 text-rose-300' : 'bg-amber-500/20 text-amber-300'}`}>
                      {f.severity}
                    </span>
                    <span className="font-mono text-white/50 text-xs">{fmtTs(f.timestampSec)}</span>
                    <span className="text-white/80">{f.note}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Top-3 strengths / improvements summary */}
      {!isLegacy && (ranks.top.length > 0 || ranks.bottom.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="panel p-5">
            <div className="font-display font-bold mb-2 text-emerald-300">Top strengths</div>
            <ul className="space-y-1 text-sm">
              {ranks.top.map((d) => (
                <li key={d.key} className="flex justify-between text-white/80">
                  <span>{d.label}</span>
                  <span className="font-mono text-emerald-300">{d.score}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="panel p-5">
            <div className="font-display font-bold mb-2 text-amber-300">Top improvement areas</div>
            <ul className="space-y-1 text-sm">
              {ranks.bottom.map((d) => (
                <li key={d.key} className="flex justify-between text-white/80">
                  <span>{d.label}</span>
                  <span className="font-mono text-amber-300">{d.score}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Detailed categories */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {CATEGORIES.map((cat) => (
          <CategoryPanel
            key={cat.id}
            category={cat}
            dims={dims}
            rationales={rationales}
            scIsLegacy={isLegacy}
          />
        ))}
      </div>

      {/* Free-text strengths/improvements coming from LLM */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="panel p-5">
          <div className="font-display font-bold mb-2 text-emerald-300">Highlights</div>
          {(sc.strengths || []).length === 0 ? (
            <div className="text-white/40 text-sm">No highlights captured.</div>
          ) : (
            <ul className="space-y-2 text-sm">
              {topN(sc.strengths, 5).map((s, i) => (
                <li key={i} className="text-white/80">• {s}</li>
              ))}
            </ul>
          )}
        </div>
        <div className="panel p-5">
          <div className="font-display font-bold mb-2 text-amber-300">Coaching notes</div>
          {(sc.improvements || []).length === 0 ? (
            <div className="text-white/40 text-sm">No improvements captured.</div>
          ) : (
            <ul className="space-y-2 text-sm">
              {topN(sc.improvements, 5).map((it, i) => (
                <li key={i} className="text-white/80">
                  <span className="font-mono text-white/50 text-xs mr-2">{fmtTs(it.timestampSec)}</span>
                  {it.note}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {sc.transcript && <TranscriptPanel transcript={sc.transcript} />}
    </div>
  );
}

function TranscriptPanel({ transcript }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="panel p-5">
      <button onClick={() => setOpen(!open)} className="font-display font-bold text-left w-full">
        Transcript {open ? '▾' : '▸'}
      </button>
      {open && (
        <pre className="mt-3 text-xs text-white/70 whitespace-pre-wrap font-mono max-h-96 overflow-auto">
          {transcript}
        </pre>
      )}
    </div>
  );
}
